import { useEffect, useState } from "react";
import { X, Flag, Paperclip, Loader2, Send, CheckCircle2, AlertTriangle, Download, Circle, CheckCircle } from "lucide-react";
import toast from "react-hot-toast";
import { axiosInstance } from "../../lib/axios";
import { useAuthStore } from "../../store/useAuthStore";

const money = (n?: number) => `$${(Math.round((n || 0) * 100) / 100).toLocaleString()}`;
const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
const fmtDateTime = (d?: string | null) => (d ? new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

// The visible lifecycle merges the funding axis (status) with the workflow
// axis (workStatus) into the spec's single status vocabulary.
const displayStatus = (m: any) => {
  const ws = m?.workStatus || "Upcoming";
  if (ws !== "Upcoming") return ws;
  if (m?.status === "Released") return "Completed";
  if (m?.status === "In Escrow") return "Ready to Start";
  return "Awaiting Funding";
};

const STATUS_STYLE: Record<string, string> = {
  "Awaiting Funding": "bg-amber-100 text-amber-700",
  "Ready to Start": "bg-primary-container/40 text-primary",
  "In Progress": "bg-primary-container/40 text-primary",
  "Submitted for Approval": "bg-violet-100 text-violet-700",
  "Changes Requested": "bg-error-container text-error",
  Approved: "bg-secondary-container text-on-secondary-container",
  Completed: "bg-secondary-container text-on-secondary-container",
  Cancelled: "bg-surface-container-highest text-on-surface-variant",
};

const readAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = reject;
    r.readAsDataURL(file);
  });

// Shared View Milestone modal for the Inbox. The BD submits the milestone for
// client approval; the client completes it (with confirmation) or requests
// changes. All actions stay scoped to the active project.
export default function InboxMilestoneModal({
  project,
  index,
  onClose,
  onUpdated,
}: {
  project: any;
  index: number;
  onClose: () => void;
  onUpdated: (project: any) => void;
}) {
  const { authUser } = useAuthStore();
  const isClient = authUser?.role === "Client";
  const [idx, setIdx] = useState(index);
  const [busy, setBusy] = useState(false);

  // BD submission form
  const [showSubmit, setShowSubmit] = useState(false);
  const [subMessage, setSubMessage] = useState("");
  const [subFiles, setSubFiles] = useState<File[]>([]);

  // Client flows
  const [showConfirm, setShowConfirm] = useState(false);
  const [showChanges, setShowChanges] = useState(false);
  const [changeReason, setChangeReason] = useState("");

  useEffect(() => setIdx(index), [index, project?._id]);

  const ms: any[] = project?.milestones || [];
  const m = ms[idx];
  if (!project || !m) return null;

  const st = displayStatus(m);
  const canSubmit = !isClient && !["Submitted for Approval", "Approved", "Completed", "Cancelled"].includes(m.workStatus || "Upcoming") && m.status !== "Released";
  const canReview = isClient && m.workStatus === "Submitted for Approval";
  const submissions = [...(m.submissions || [])].reverse();
  const changeRequests = [...(m.changeRequests || [])].reverse();

  const act = async (path: string, body: any, okMsg: string) => {
    setBusy(true);
    try {
      const { data } = await axiosInstance.post(`/project/${project._id}/milestone/${idx}/${path}`, body);
      toast.success(okMsg);
      onUpdated(data.project);
      setShowSubmit(false); setShowConfirm(false); setShowChanges(false);
      setSubMessage(""); setSubFiles([]); setChangeReason("");
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const doSubmit = async () => {
    if (!subMessage.trim()) { toast.error("A submission message is required."); return; }
    const files = await Promise.all(subFiles.map(async (f) => ({ data: await readAsDataUrl(f), fileName: f.name, fileSize: f.size })));
    await act("submit", { message: subMessage, files }, "Milestone submitted for client approval.");
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-surface-container-lowest w-full max-w-2xl max-h-[88vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-outline-variant flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Flag size={18} className="text-primary shrink-0" />
              <h3 className="text-[17px] font-bold text-on-surface truncate">Milestone {idx + 1}: {m.title}</h3>
            </div>
            <p className="text-[12px] text-on-surface-variant mt-0.5">{project.name}</p>
            <span className={`inline-block mt-2 text-[11px] font-bold px-2.5 py-0.5 rounded-full ${STATUS_STYLE[st] || "bg-surface-container-highest text-on-surface-variant"}`}>{st}</span>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-outline hover:bg-surface-container transition-colors shrink-0"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto cre-scroll px-6 py-4 space-y-5">
          {/* Milestone switcher */}
          {ms.length > 1 && (
            <div className="flex gap-1.5 flex-wrap">
              {ms.map((mm: any, i: number) => (
                <button key={i} onClick={() => setIdx(i)} className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${i === idx ? "bg-primary text-on-primary border-primary" : "border-outline-variant text-on-surface-variant hover:bg-surface-container"}`}>
                  {i + 1}. {mm.title.length > 18 ? mm.title.slice(0, 18) + "…" : mm.title}
                </button>
              ))}
            </div>
          )}

          {/* Core info */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-surface-container-low rounded-xl p-3 border border-outline-variant/30">
              <p className="text-[10px] text-outline uppercase font-bold tracking-wider mb-1">Amount</p>
              <p className="text-[15px] font-bold text-on-surface">{money(m.amount)}</p>
            </div>
            <div className="bg-surface-container-low rounded-xl p-3 border border-outline-variant/30">
              <p className="text-[10px] text-outline uppercase font-bold tracking-wider mb-1">Expected</p>
              <p className="text-[13px] font-semibold text-on-surface">{fmtDate(m.dueDate)}</p>
            </div>
            <div className="bg-surface-container-low rounded-xl p-3 border border-outline-variant/30">
              <p className="text-[10px] text-outline uppercase font-bold tracking-wider mb-1">Funding</p>
              <p className="text-[13px] font-semibold text-on-surface">{m.status === "Released" ? "Released" : m.status === "In Escrow" ? "Funded (escrow)" : "Not funded"}</p>
            </div>
          </div>

          {/* Scope */}
          {(m.scope || m.notes) && (
            <div>
              <p className="text-[11px] text-outline uppercase font-bold tracking-wider mb-1.5">Scope</p>
              <p className="text-[13px] text-on-surface-variant whitespace-pre-wrap">{m.scope || m.notes}</p>
            </div>
          )}

          {/* Deliverables */}
          {(m.deliverables || []).length > 0 && (
            <div>
              <p className="text-[11px] text-outline uppercase font-bold tracking-wider mb-1.5">Deliverables</p>
              <div className="space-y-1.5">
                {m.deliverables.map((d: any, i: number) => (
                  <div key={i} className="flex items-center gap-2 text-[13px] text-on-surface">
                    {d.done ? <CheckCircle size={15} className="text-green-500 shrink-0" /> : <Circle size={15} className="text-outline shrink-0" />}
                    <span className={d.done ? "" : "text-on-surface-variant"}>{d.title}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Delivered work synced from ERP (deliverables + completion) */}
          {(m.completionDate || m.erpStatus || (m.erpDeliverables || []).length > 0) && (
            <div>
              <p className="text-[11px] text-outline uppercase font-bold tracking-wider mb-1.5">Delivered Work (from ERP)</p>
              <div className="bg-surface-container-low rounded-xl p-3 border border-outline-variant/30 space-y-2">
                <div className="flex items-center gap-4 text-[12px] text-on-surface-variant flex-wrap">
                  {m.erpStatus && <span>ERP: <b className="text-on-surface">{m.erpStatus}</b></span>}
                  {m.completionDate && <span>Completed: <b className="text-on-surface">{fmtDate(m.completionDate)}</b></span>}
                </div>
                {(m.erpDeliverables || []).length > 0 && (
                  <div className="space-y-1">
                    {m.erpDeliverables.map((d: any, i: number) => (
                      <a key={i} href={d.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[12px] text-primary hover:underline">
                        <Download size={13} /> {d.name || d.url}{d.type ? <span className="text-outline">· {d.type}</span> : null}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Change requests */}
          {changeRequests.length > 0 && (
            <div>
              <p className="text-[11px] text-outline uppercase font-bold tracking-wider mb-1.5">Requested Changes</p>
              <div className="space-y-2">
                {changeRequests.map((c: any, i: number) => (
                  <div key={i} className="bg-error-container/30 border border-error/20 rounded-xl p-3">
                    <p className="text-[13px] text-on-surface whitespace-pre-wrap">{c.reason}</p>
                    <p className="text-[11px] text-outline mt-1">{fmtDateTime(c.requestedAt)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Submission history */}
          {submissions.length > 0 && (
            <div>
              <p className="text-[11px] text-outline uppercase font-bold tracking-wider mb-1.5">Submission History</p>
              <div className="space-y-2">
                {submissions.map((s: any, i: number) => (
                  <div key={i} className="bg-surface-container-low rounded-xl p-3 border border-outline-variant/30">
                    <p className="text-[13px] text-on-surface whitespace-pre-wrap">{s.message}</p>
                    <p className="text-[11px] text-outline mt-1">Submitted {fmtDateTime(s.submittedAt)}</p>
                    {(s.files || []).length > 0 && (
                      <div className="mt-2 space-y-1">
                        {s.files.map((f: any, j: number) => (
                          <a key={j} href={f.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[12px] text-primary hover:underline">
                            <Download size={13} /> {f.name} {f.size ? <span className="text-outline">({(f.size / 1024 / 1024).toFixed(1)} MB)</span> : null}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* BD: submission form */}
          {showSubmit && (
            <div className="bg-primary-container/10 border border-primary/25 rounded-xl p-4 space-y-3">
              <p className="text-[13px] font-bold text-on-surface">Submit for Client Approval</p>
              <textarea
                value={subMessage}
                onChange={(e) => setSubMessage(e.target.value)}
                rows={3}
                placeholder="Describe what was completed and what the client should review… (required)"
                className="w-full rounded-lg border border-outline-variant bg-surface-container-lowest px-3 py-2 text-[13px] outline-none focus:border-primary"
              />
              <div className="flex items-center gap-3">
                <label className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-primary cursor-pointer hover:underline">
                  <Paperclip size={14} /> Attach files
                  <input type="file" multiple className="hidden" onChange={(e) => setSubFiles([...subFiles, ...Array.from(e.target.files || [])])} />
                </label>
                {subFiles.map((f, i) => (
                  <span key={i} className="text-[11px] bg-surface-container px-2 py-0.5 rounded-full text-on-surface-variant">
                    {f.name} <button onClick={() => setSubFiles(subFiles.filter((_, j) => j !== i))} className="text-error font-bold ml-1">×</button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Client: request changes form */}
          {showChanges && (
            <div className="bg-error-container/20 border border-error/25 rounded-xl p-4 space-y-3">
              <p className="text-[13px] font-bold text-on-surface">Request Changes</p>
              <textarea
                value={changeReason}
                onChange={(e) => setChangeReason(e.target.value)}
                rows={3}
                placeholder="What needs to change before you can approve this milestone? (required)"
                className="w-full rounded-lg border border-outline-variant bg-surface-container-lowest px-3 py-2 text-[13px] outline-none focus:border-error"
              />
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="px-6 py-4 border-t border-outline-variant flex items-center justify-end gap-2 bg-surface-container-lowest">
          {canSubmit && !showSubmit && (
            <button onClick={() => setShowSubmit(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-on-primary text-[13px] font-bold hover:brightness-110 transition-all">
              <Send size={15} /> Submit for Client Approval
            </button>
          )}
          {canSubmit && showSubmit && (
            <>
              <button onClick={() => setShowSubmit(false)} disabled={busy} className="px-4 py-2 rounded-lg border border-outline-variant text-[13px] font-semibold text-on-surface-variant hover:bg-surface-container">Cancel</button>
              <button onClick={doSubmit} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-on-primary text-[13px] font-bold hover:brightness-110 transition-all disabled:opacity-60">
                {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Submit
              </button>
            </>
          )}

          {canReview && !showChanges && (
            <>
              <button onClick={() => setShowChanges(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-outline-variant text-on-surface-variant text-[13px] font-semibold hover:bg-surface-container transition-colors">
                <AlertTriangle size={15} /> Request Changes
              </button>
              <button onClick={() => setShowConfirm(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-on-primary text-[13px] font-bold hover:brightness-110 transition-all">
                <CheckCircle2 size={15} /> Complete Milestone
              </button>
            </>
          )}
          {canReview && showChanges && (
            <>
              <button onClick={() => setShowChanges(false)} disabled={busy} className="px-4 py-2 rounded-lg border border-outline-variant text-[13px] font-semibold text-on-surface-variant hover:bg-surface-container">Cancel</button>
              <button
                onClick={() => changeReason.trim() ? act("request-changes", { reason: changeReason }, "Change request sent to the project team.") : toast.error("Please describe the changes you need.")}
                disabled={busy}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-error text-white text-[13px] font-bold hover:brightness-110 transition-all disabled:opacity-60"
              >
                {busy ? <Loader2 size={15} className="animate-spin" /> : <AlertTriangle size={15} />} Send Request
              </button>
            </>
          )}
          {!canSubmit && !canReview && <button onClick={onClose} className="px-4 py-2 rounded-lg border border-outline-variant text-[13px] font-semibold text-on-surface-variant hover:bg-surface-container">Close</button>}
        </div>
      </div>

      {/* Client confirmation dialog — completing releases the secured amount */}
      {showConfirm && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={() => setShowConfirm(false)}>
          <div className="bg-surface-container-lowest w-full max-w-md rounded-2xl shadow-2xl p-6" onClick={(e) => e.stopPropagation()}>
            <h4 className="text-[16px] font-bold text-on-surface mb-2">Complete Milestone?</h4>
            <p className="text-[13px] text-on-surface-variant mb-3">
              You are about to approve <span className="font-bold text-on-surface">Milestone {idx + 1} — {m.title}</span> on <span className="font-bold text-on-surface">{project.name}</span>.
            </p>
            <div className="bg-surface-container-low rounded-xl p-3 border border-outline-variant/30 mb-3">
              <p className="text-[12px] text-on-surface-variant">Milestone Amount</p>
              <p className="text-[18px] font-bold text-on-surface">{money(m.amount)}</p>
            </div>
            <p className="text-[12px] text-on-surface-variant mb-4">
              By confirming, you acknowledge the milestone deliverables were completed according to the agreed scope.
              {project.source === "Direct" ? ` Confirming will complete the milestone and release ${money(m.amount)} from the secured milestone funds.` : " The approval will be recorded against the project's payment records."}
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowConfirm(false)} disabled={busy} className="px-4 py-2 rounded-lg border border-outline-variant text-[13px] font-semibold text-on-surface-variant hover:bg-surface-container">Cancel</button>
              <button
                onClick={() => act("complete", {}, "Milestone completed — the amount has been released.")}
                disabled={busy}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-on-primary text-[13px] font-bold hover:brightness-110 transition-all disabled:opacity-60"
              >
                {busy ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Confirm &amp; Complete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
